// Procedural timeline. The matter doesn't carry a discrete `timeline` field
// in the current data layer — instead we derive lanes from the practice
// area's `pipeline` (e.g. asylum: I-589 → BIO → MASTER → INDIVIDUAL → DECISION)
// and highlight the current stage. Past stages render as completed, the
// current as active, future as pending. Hovers show stage labels.

import type { Matter, PracticeArea } from "../../data/types";

interface Props {
  matter: Matter;
  pa: PracticeArea | null;
}

function findStageIndex(pipeline: string[], stage: string | undefined): number {
  if (!stage) return 0;
  const lower = stage.toLowerCase();
  // Exact match first
  const exact = pipeline.findIndex((s) => s.toLowerCase() === lower);
  if (exact !== -1) return exact;
  // Substring fallback
  const partial = pipeline.findIndex(
    (s) => lower.includes(s.toLowerCase()) || s.toLowerCase().includes(lower),
  );
  return partial === -1 ? 0 : partial;
}

export function SwimLanes({ matter, pa }: Props) {
  if (!pa || !pa.pipeline || pa.pipeline.length === 0) return null;
  const stageIndex = findStageIndex(pa.pipeline, matter.stage);
  const total = pa.pipeline.length;

  return (
    <div className="cc-card" style={{ marginBottom: 14 }}>
      <div className="cc-card-head">
        <div className="cc-card-title">Procedural timeline</div>
        <div className="cc-card-sub">
          · {pa.label} pipeline ·{" "}
          <span style={{ color: "var(--pa-accent-strong, var(--ink-2))", fontWeight: 600 }}>
            {pa.pipeline[stageIndex]}
          </span>
        </div>
      </div>
      <div style={{ padding: "16px 18px 18px" }}>
        <div
          style={{
            position: "relative",
            display: "grid",
            gridTemplateColumns: `repeat(${total}, 1fr)`,
            gap: 0,
          }}
        >
          {/* Connector line */}
          <div
            style={{
              position: "absolute",
              top: 11,
              left: 12,
              right: 12,
              height: 2,
              background: "var(--line)",
              zIndex: 0,
            }}
          />
          {/* Active connector */}
          <div
            style={{
              position: "absolute",
              top: 11,
              left: 12,
              width:
                total > 1
                  ? `calc(${(stageIndex / (total - 1)) * 100}% - 12px + ${stageIndex === 0 ? "0px" : "0px"})`
                  : "0",
              height: 2,
              background: "var(--pa-accent, var(--muted))",
              zIndex: 1,
              transition: "width 0.3s ease",
            }}
          />
          {pa.pipeline.map((stageName, i) => {
            const completed = i < stageIndex;
            const active = i === stageIndex;
            const dotBg = completed
              ? "var(--pa-accent, var(--muted))"
              : active
                ? "var(--olive)"
                : "var(--bg-elev)";
            const dotBorder = completed
              ? "var(--pa-accent, var(--muted))"
              : active
                ? "var(--olive)"
                : "var(--line)";
            return (
              <div
                key={i}
                style={{
                  position: "relative",
                  zIndex: 2,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 8,
                  minWidth: 0,
                }}
              >
                <span
                  style={{
                    width: active ? 14 : 10,
                    height: active ? 14 : 10,
                    borderRadius: 999,
                    background: dotBg,
                    border: `2px solid ${dotBorder}`,
                    boxShadow: active ? "0 0 0 4px var(--olive-soft)" : "none",
                    transition: "all 0.18s",
                  }}
                />
                <div
                  style={{
                    fontSize: 10,
                    fontWeight: active ? 700 : 500,
                    color: active ? "var(--olive)" : completed ? "var(--ink-2)" : "var(--muted)",
                    textAlign: "center",
                    lineHeight: 1.3,
                    padding: "0 4px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: "100%",
                  }}
                  title={stageName}
                >
                  {stageName}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
